import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactApexChart from 'react-apexcharts'
import ChartInteractions from '../Progression/ChartInteractions'
import { useChartZoomSampling } from '../Progression/hooks'
import useContainerWidth from '../../hooks/useContainerWidth'
import {
  buildChartBaseOptions, withCategories,
  AXIS_LABEL_STYLE, AXIS_TITLE_STYLE, DS_COLORS
} from '../charts/config'
import ChartEmptyState from '../shared/ChartEmptyState'
import Loader from '../shared/Loader'
import { VIEW_MODES } from './constants'

const CHART_ID = 'statistics-evolution'

const EMPTY_MESSAGE = 'L\'évolution de la note apparaîtra ici une fois les données disponibles.'

const LOADING_OVERLAY_CLASSES = 'tw:absolute tw:inset-0 tw:flex tw:items-center '
  + 'tw:justify-center tw:bg-white/70 tw:z-10'

const yLabelFormatter = isSatisfaction => {
  if (isSatisfaction)
    return value => `${Math.round(value)} %`
  return value => Number(value).toFixed(2)
}

const baseOptionsFor = ({ isSatisfaction, gradeMax }) => ({
  ...buildChartBaseOptions('area', CHART_ID),
  colors: [DS_COLORS.info],
  stroke: { curve: 'smooth', width: 2 },
  fill: { type: 'gradient', gradient: { opacityFrom: 0.45, opacityTo: 0.1 } },
  dataLabels: { enabled: false },
  yaxis: {
    max: isSatisfaction ? 100 : gradeMax,
    min: 0,
    tickAmount: 4,
    title: {
      text: isSatisfaction ? 'Taux de Satisfaction (%)' : 'Note globale',
      style: AXIS_TITLE_STYLE,
      offsetX: -6
    },
    labels: { formatter: yLabelFormatter(isSatisfaction), style: AXIS_LABEL_STYLE }
  }
})

const bucketsFor = (viewMode, evolution) => {
  const selected = viewMode === VIEW_MODES.SATISFACTION ? evolution.satisfaction : evolution.grade
  return selected || []
}

const EvolutionChart = ({ evolution, viewMode, gradeMax, isLoading = false }) => {
  const containerRef = useRef(null)
  const containerWidth = useContainerWidth(containerRef)
  const isSatisfaction = viewMode === VIEW_MODES.SATISFACTION

  const buckets = useMemo(() => bucketsFor(viewMode, evolution), [viewMode, evolution])
  const handleZoomed = useChartZoomSampling(buckets, containerWidth)

  const options = useMemo(() => {
    const base = baseOptionsFor({ isSatisfaction, gradeMax })
    const valueSuffix = isSatisfaction ? ' %' : ` / ${gradeMax}`
    const opts = withCategories(base, buckets, { containerWidth, valueSuffix })
    const chart = {
      ...opts.chart,
      parentHeightOffset: 0,
      events: { ...opts.chart.events, zoomed: handleZoomed }
    }
    return { ...opts, chart }
  }, [buckets, containerWidth, gradeMax, handleZoomed, isSatisfaction])

  const series = useMemo(() => [{
    name: isSatisfaction ? 'Taux de satisfaction' : 'Note globale',
    data: buckets.map(bucket => bucket.value)
  }], [buckets, isSatisfaction])

  const showEmpty = buckets.length === 0 && !isLoading
  let body = <ChartEmptyState message={EMPTY_MESSAGE} />
  if (!showEmpty)
    body = <ReactApexChart height={340} options={options} series={series} type="area" />

  // Mount the ref'd container even when empty so useContainerWidth can attach
  // Its ResizeObserver — otherwise sampling stays disabled on first data load.
  return (
    <>
      <div ref={containerRef} className="tw:relative tw:[&_.apexcharts-toolbar]:hidden!">
        {body}
        {isLoading ? (
          <div className={LOADING_OVERLAY_CLASSES}>
            <Loader />
          </div>
        ) : null}
      </div>
      {showEmpty ? null : (
        <div className="tw:flex">
          <ChartInteractions chartId={CHART_ID} />
        </div>
      )}
    </>
  )
}

EvolutionChart.propTypes = {
  evolution: PropTypes.shape({
    grade: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number
    })),
    satisfaction: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number
    }))
  }).isRequired,
  gradeMax: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired
}

EvolutionChart.defaultProps = {
  isLoading: false
}

export default EvolutionChart
